import styled from '@emotion/styled';
import moment, { Moment } from 'moment';
import React, { MutableRefObject, useEffect, useRef, useState } from 'react';

import iconArrowBack from 'src/public/img/icon-arrow-back.svg';
import iconArrowForward from 'src/public/img/icon-arrow-forward.svg';
import { color, font } from 'src/shared/styles/variables';

import { DateWithSessionSlots } from '../models/videoSessions';

const MonthYearText = styled.h4({
    color: color.ebonyClay,
    textAlign: 'center',
    marginBottom: 0,
    marginTop: 20,
    ...font.black(font.size.large),
    textTransform: 'capitalize',
});

const Wrapper = styled.div({
    width: '100%',
    display: 'flex',
    marginTop: 25,
    borderBottom: `1px solid ${color.linkWaterLight}`,
});

const ChangeSlideButton = styled.div({
    marginTop: 16,
    height: 32,
    minWidth: 32,
    borderRadius: 16,
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
});

const DatesContainer = styled.div<{ addMarginLeft: boolean }>(({ addMarginLeft }) => ({
    width: '100%',
    display: 'flex',
    overflow: 'hidden',
    marginLeft: addMarginLeft ? 32 : 0,
}));

const ArrowImage = styled.img({
    height: 32,
    width: 32,
});

interface DateRecordContainerProps {
    marginLeft: number;
}

const DateRecordContainer = styled.div<DateRecordContainerProps>(({ marginLeft }) => ({
    minWidth: 95,
    marginRight: 16,
    transition: '.3s all',
    marginLeft,
}));

interface DayNameProps {
    highlighted: boolean;
}

const DayName = styled.p<DayNameProps>(
    props => ({
        color: props.highlighted ? color.royalBlue : color.steelGray,
        textTransform: 'uppercase',
        textAlign: 'center',
    }),
    font.heavy(font.size.base),
);

const DayNumber = styled.p<DayNameProps>(
    props => ({
        color: props.highlighted ? color.royalBlue : color.pickledBluewood,
        textAlign: 'center',
        marginBottom: 15,
    }),
    font.black(font.size.extraLarge),
);

interface DateRecordProps {
    date: Moment;
    highlighted: boolean;
    marginLeft: number;
}

const DateRecord: React.FC<DateRecordProps> = ({ date, marginLeft, highlighted }) => {
    const formattedDate = date.format('DD MM YYYY');
    const dayName = moment(formattedDate, 'DD MM YYYY').format('ddd').replace('.', '');
    const dayNumber = date.format('DD');

    return (
        <DateRecordContainer marginLeft={marginLeft}>
            <DayName highlighted={highlighted}>{dayName}</DayName>
            <DayNumber highlighted={highlighted}>{dayNumber}</DayNumber>
        </DateRecordContainer>
    );
};

interface Props {
    dates: DateWithSessionSlots[];
    sliderOffset: number;
    sliderWidth: number;
    onGoToPreviousWeekClick: () => void;
    onGoToNextWeekClick: () => void;
    onSliderWidthChange: (width: number) => void;
}

export const ScheduleCalendarDatesHeader: React.FC<Props> = props => {
    const slidesContainerRef = useRef(null);

    const [firstVisibleDay, setFirstVisibleDay] = useState<null | Moment>(props.dates[0]?.date);

    useEffect(() => {
        const element = (slidesContainerRef as MutableRefObject<any>).current;

        const width = element ? element.offsetWidth : 0;

        props.onSliderWidthChange(width);
    }, [slidesContainerRef.current]);

    useEffect(() => {
        const page = Math.round(props.sliderOffset / -props.sliderWidth) || 0;
        const newFirstVisibleDay = props.dates[7 * page]?.date;

        if (newFirstVisibleDay) {
            setFirstVisibleDay(newFirstVisibleDay);
        }
    }, [props.sliderOffset, props.sliderWidth]);

    const isToday = firstVisibleDay?.isSame(moment(), 'day') ?? false;

    return (
        <>
            {firstVisibleDay ? (
                <MonthYearText>
                    {firstVisibleDay.format('MMMM')} {firstVisibleDay.format('YYYY')}
                </MonthYearText>
            ) : null}
            <Wrapper>
                {!isToday && (
                    <ChangeSlideButton onClick={props.onGoToPreviousWeekClick}>
                        <ArrowImage src={iconArrowBack} />
                    </ChangeSlideButton>
                )}

                <DatesContainer ref={slidesContainerRef} addMarginLeft={isToday}>
                    {props.dates.map(({ date }, i) => (
                        <DateRecord
                            key={date.toString()}
                            date={date}
                            marginLeft={i === 0 ? props.sliderOffset : 0}
                            highlighted={i === 0}
                        />
                    ))}
                </DatesContainer>

                <ChangeSlideButton onClick={props.onGoToNextWeekClick}>
                    <ArrowImage src={iconArrowForward} />
                </ChangeSlideButton>
            </Wrapper>
        </>
    );
};
